import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { CheckCircle, Clock, XCircle, AlertTriangle } from "lucide-react";

const eligibilityBadgeVariants = cva(
  "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors",
  {
    variants: {
      status: {
        eligible:
          "border-transparent bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
        pending:
          "border-transparent bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
        ineligible:
          "border-transparent bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
        warning:
          "border-transparent bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400",
      },
    },
    defaultVariants: {
      status: "pending",
    },
  }
);

export type EligibilityStatus = "eligible" | "pending" | "ineligible" | "warning";

export interface EligibilityBadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof eligibilityBadgeVariants> {
  /** The eligibility status to display */
  status: EligibilityStatus;
  /** Optional label to override the default status label */
  label?: string;
  /** Optional tooltip content for additional context */
  tooltip?: string;
  /** Whether to show the status icon */
  showIcon?: boolean;
}

const statusIcons = {
  eligible: CheckCircle,
  pending: Clock,
  ineligible: XCircle,
  warning: AlertTriangle,
};

const defaultLabels = {
  eligible: "Eligible",
  pending: "Pending",
  ineligible: "Ineligible",
  warning: "Warning",
};

function EligibilityBadge({
  className,
  status,
  label,
  tooltip,
  showIcon = true,
  ...props
}: EligibilityBadgeProps) {
  const Icon = statusIcons[status];
  const displayLabel = label || defaultLabels[status];

  const badge = (
    <div
      className={cn(eligibilityBadgeVariants({ status }), className)}
      {...props}
    >
      {showIcon && <Icon className="h-3 w-3" />}
      <span>{displayLabel}</span>
    </div>
  );

  if (tooltip) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>{badge}</TooltipTrigger>
          <TooltipContent>
            <p className="max-w-xs text-sm">{tooltip}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return badge;
}

export { EligibilityBadge, eligibilityBadgeVariants };
